import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Input, Card, Button, message, Spin } from "antd";
import apiClient from "../../actions/api";
import { removeBid } from "../../actions/bid";
import { updateBalance } from "../../actions/auth";
import openNotification from "../../components/Notifications/openNotification";
import isEmpty from "../../validations/is-empty";

// import assets
import ResumePreviewSrc from "../../assets/image/preview.png";

// import style
import "../../style/bid/resumePreview.scss";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const { TextArea } = Input;

const normalizeResumeText = (value) =>
  String(value || "")
    .replace(/^[\s•·▪◦‣⁃\-*–—]+/, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const findResumeLineBlock = (lines, value) => {
  const target = normalizeResumeText(value);
  if (!target) return [];
  for (let start = 0; start < lines.length; start += 1) {
    const block = [];
    for (let end = start; end < lines.length; end += 1) {
      const line = normalizeResumeText(lines[end]);
      if (!line) break;
      block.push(line);
      const combined = block.join(" ");
      if (combined === target) {
        return Array.from({ length: end - start + 1 }, (_, offset) => start + offset);
      }
      if (!target.startsWith(`${combined} `)) break;
    }
  }
  return [];
};

const splitResumePreviewText = (value, count) => {
  const text = String(value || "").trim();
  if (count <= 1 || !text) return [text];
  const explicitLines = text.split(/\r?\n+/).map((line) => line.trim()).filter(Boolean);
  const sentences = explicitLines.length > 1
    ? explicitLines
    : text.match(/[^.!?]+(?:[.!?]+|$)/g)?.map((item) => item.trim()).filter(Boolean) || [text];
  const result = [];
  let sentenceIndex = 0;
  for (let lineIndex = 0; lineIndex < count; lineIndex += 1) {
    const take = Math.max(
      1,
      Math.ceil((sentences.length - sentenceIndex) / (count - lineIndex)),
    );
    result.push(sentences.slice(sentenceIndex, sentenceIndex + take).join(" "));
    sentenceIndex += take;
  }
  return result;
};

const replaceResumeSubstring = (line, original, replacement) => {
  const escaped = String(original || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (!escaped) return line;
  return String(line || "").replace(
    new RegExp(escaped.replace(/\s+/g, "\\s+"), "i"),
    replacement,
  );
};

const tokenizeResumeText = (value) =>
  String(value || "").match(
    /\s+|[\p{L}\p{N}]+(?:['’.-][\p{L}\p{N}]+)*|[^\s\p{L}\p{N}]/gu,
  ) || [];

// Token-level LCS marks complete words while retaining whitespace and
// punctuation, making resume changes much easier to scan than character diffs.
const diffWords = (before = "", after = "") => {
  const beforeTokens = tokenizeResumeText(before);
  const afterTokens = tokenizeResumeText(after);
  const rows = beforeTokens.length + 1;
  const columns = afterTokens.length + 1;
  const matrix = Array.from({ length: rows }, () => new Uint16Array(columns));
  for (let row = beforeTokens.length - 1; row >= 0; row -= 1) {
    for (let column = afterTokens.length - 1; column >= 0; column -= 1) {
      matrix[row][column] =
        beforeTokens[row] === afterTokens[column]
          ? matrix[row + 1][column + 1] + 1
          : Math.max(matrix[row + 1][column], matrix[row][column + 1]);
    }
  }
  const changes = [];
  const push = (type, character) => {
    const last = changes[changes.length - 1];
    if (last?.type === type) last.text += character;
    else changes.push({ type, text: character });
  };
  let row = 0;
  let column = 0;
  while (row < beforeTokens.length && column < afterTokens.length) {
    if (beforeTokens[row] === afterTokens[column]) {
      push("same", beforeTokens[row]);
      row += 1;
      column += 1;
    } else if (matrix[row + 1][column] >= matrix[row][column + 1]) {
      push("removed", beforeTokens[row]);
      row += 1;
    } else {
      push("added", afterTokens[column]);
      column += 1;
    }
  }
  while (row < beforeTokens.length) push("removed", beforeTokens[row++]);
  while (column < afterTokens.length) push("added", afterTokens[column++]);
  return changes;
};

const ResumePreview = ({ title }) => {
  // set title
  document.title = title;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { bidId } = useParams();

  const result = useSelector((state) => state?.bid?.resume_draft);
  const storedBid = useSelector((state) => state?.bid?.bid);
  const user = useSelector((state) => state?.auth?.user);
  const [bid, setBid] = useState(storedBid);
  const [draftLoading, setDraftLoading] = useState(Boolean(bidId));

  const [companyName, setCompanyName] = useState(result?.company_name || "");
  const [developerTitle, setDeveloperTitle] = useState(
    result?.developer_title || "",
  );
  const [roleTitle, setRoleTitle] = useState(result?.role_title || "");
  const [jobType, setJobType] = useState(result?.job_type || "");
  const [salaryRange, setSalaryRange] = useState(result?.salary_range || "");

  const [summary, setSummary] = useState(result?.summary || "");
  const [skills, setSkills] = useState(result?.skills || {});
  const [experiences, setExperiences] = useState(
    result?.experiences || {
      role1: "",
      experience1: [],
      role2: "",
      experience2: [],
      role3: "",
      experience3: [],
    },
  );
  const [previewLoading, setPreviewLoading] = useState(false);
  const [optimization, setOptimization] = useState(result || {});
  const [documentCompanyName, setDocumentCompanyName] = useState(
    result?.company_name || "",
  );
  const isResumeDocument = bid?.source_type === "resume_document";
  const employmentPreferences =
    Array.isArray(user?.employment_preferences) &&
    user.employment_preferences.length >= 2
      ? user.employment_preferences
      : [{}, {}];

  useEffect(() => {
    if (!bidId && isEmpty(result)) navigate("/bid", { replace: true });
  }, [bidId, navigate, result]);

  useEffect(() => {
    if (!bidId) return;

    const loadSavedDraft = async () => {
      try {
        const response = await apiClient.post("/bids/get-bid-item", {
          id: bidId,
        });
        const savedBid = response.data?.result;
        const savedResult = JSON.parse(savedBid?.bid);

        setBid(savedBid);
        setCompanyName(savedResult?.company_name || "");
        setDeveloperTitle(savedResult?.developer_title || "");
        setRoleTitle(savedResult?.role_title || "");
        setJobType(savedResult?.job_type || "");
        setSalaryRange(savedResult?.salary_range || "");
        setSummary(savedResult?.summary || "");
        setSkills(savedResult?.skills || {});
        const savedExperiences = savedResult?.experiences || {
          role1: "",
          experience1: [],
          role2: "",
          experience2: [],
          role3: "",
          experience3: [],
        };
        setExperiences(savedExperiences);
      } catch (err) {
        openNotification(
          "error",
          "Unable to load resume draft",
          err?.response?.data?.errors?.[0]?.msg ||
            "The saved bid content is invalid or unavailable.",
        );
        navigate("/dashboard", { replace: true });
      } finally {
        setDraftLoading(false);
      }
    };

    loadSavedDraft();
  }, [bidId, navigate]);

  const handleInputChange = (setter) => (e) => setter(e.target.value);

  const handleSkillChange = (category, index) => (e) => {
    setSkills((prevSkills) => ({
      ...prevSkills,
      [category]: prevSkills[category].map((skill, i) =>
        i === index ? e.target.value : skill,
      ),
    }));
  };

  const addSkillEntry = (category) => {
    setSkills((prev) => ({
      ...prev,
      [category]: [...(prev[category] || []), ""],
    }));
  };

  const removeSkillEntry = (category, index) => {
    setSkills((prev) => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index),
    }));
  };

  const handleExperienceRoleChange = (roleKey) => (e) => {
    setExperiences((prev) => ({
      ...prev,
      [roleKey]: e.target.value,
    }));
  };

  const handleExperienceChange = (key, index) => (e) => {
    setExperiences((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((exp, i) =>
        i === index ? e.target.value : exp,
      ),
    }));
  };

  const addExperienceEntry = (key) => {
    setExperiences((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), ""],
    }));
  };

  const removeExperienceEntry = (key, index) => {
    setExperiences((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== index),
    }));
  };

  const handleDownloadPDF = async (pdf_url, filename) => {
    const response = await fetch(`${API_BASE_URL}/job/description/${pdf_url}`, {
      method: "GET",
    });

    if (!response.ok) {
      // handle error
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  // Generate JSON from state
  const handleGenerateFinalizeResume = async () => {
    const params = {
      company_name: companyName.replace(/\.$/, ""),
      developer_title: developerTitle,
      role_title: roleTitle,
      salary_range: salaryRange,
      job_type: jobType,
      summary: summary,
      skills: skills,
      experiences: experiences,
      bid,
      user,
    };
    // validateData(params);

    setPreviewLoading(true);
    await apiClient
      .post("/bids/gen-resume", params)
      .then(async (res) => {
        if (res.data.status === "success") {
          dispatch(updateBalance(res.data.balance));
          // const downloadLink = res.data.downloadLink;
          const downloadPDFLink = res.data.downloadPDFLink;
          const pdf_filename = res.data.pdf_filename;

          // await triggerDownload(downloadLink, "resume.docx");
          await handleDownloadPDF(downloadPDFLink, pdf_filename);

          message.success(res.data.msg);
          dispatch(removeBid());
          navigate("/bid");
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
      })
      .finally(() => {
        setPreviewLoading(false);
      });
    return params;
  };

  const updateOptimizedBullet = (employmentIndex, bulletIndex, value) => {
    setOptimization((current) => ({
      ...current,
      employment_changes: (current.employment_changes || []).map(
        (employment, index) =>
          index === employmentIndex
            ? {
                ...employment,
                optimized_bullets: (employment.optimized_bullets || []).map(
                  (bullet, offset) => (offset === bulletIndex ? value : bullet),
                ),
              }
            : employment,
      ),
    }));
  };

  const updateOptimizedRole = (employmentIndex, value) => {
    setOptimization((current) => ({
      ...current,
      employment_changes: (current.employment_changes || []).map(
        (employment, index) =>
          index === employmentIndex
            ? { ...employment, optimized_role: value }
            : employment,
      ),
    }));
  };

  const updateOptimizedSkill = (skillIndex, value) => {
    setOptimization((current) => ({
      ...current,
      skills_changes: {
        ...(current.skills_changes || {}),
        optimized_skills: (current.skills_changes?.optimized_skills || []).map(
          (skill, index) => (index === skillIndex ? value : skill),
        ),
      },
    }));
  };

  const updateOptimizationField = (field, value) => {
    setOptimization((current) => ({ ...current, [field]: value }));
  };

  const finalizeResumeDocument = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await apiClient.post("/bids/gen-resume-document", {
        company_name: documentCompanyName.trim(),
        optimization,
        bid,
      });
      dispatch(updateBalance(data.balance));
      await handleDownloadPDF(data.downloadPDFLink, data.pdf_filename);
      message.success(data.msg);
      dispatch(removeBid());
      navigate("/bid");
    } catch (error) {
      const errors = error?.response?.data?.errors;
      (Array.isArray(errors)
        ? errors
        : [{ msg: "Unable to finalize resume." }]
      ).forEach(({ msg }) =>
        openNotification("error", "Unable to finalize", msg),
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  if (draftLoading) {
    return (
      <div className="resumePreviewSection">
        <Spin size="large" />
      </div>
    );
  }

  if (isResumeDocument) {
    const skillChanges = optimization.skills_changes || {};
    const originalLines = optimization.original_resume_lines || [];
    const bulletChanges = new Map();
    const roleChangesByLine = new Map();
    (optimization.employment_changes || []).forEach((employment) => {
      if (employment.original_role) {
        const normalizedEmployer = normalizeResumeText(employment.employer);
        const normalizedRole = normalizeResumeText(employment.original_role);
        const employerIndex = originalLines.findIndex((line) => {
          const normalizedLine = normalizeResumeText(line);
          return (
            normalizedEmployer &&
            (normalizedLine === normalizedEmployer ||
              normalizedLine.includes(normalizedEmployer))
          );
        });
        const roleIndexes = originalLines
          .map((line, index) =>
            normalizeResumeText(line).includes(normalizedRole) ? index : -1,
          )
          .filter((index) => index >= 0);
        const roleIndex =
          employerIndex >= 0
            ? roleIndexes.sort(
                (left, right) =>
                  Math.abs(left - employerIndex) -
                  Math.abs(right - employerIndex),
              )[0]
            : roleIndexes[0];
        if (roleIndex !== undefined) {
          roleChangesByLine.set(
            roleIndex,
            replaceResumeSubstring(
              originalLines[roleIndex],
              employment.original_role,
              employment.optimized_role || employment.original_role,
            ),
          );
        }
      }
      (employment.original_bullets || []).forEach((original, index) => {
        const optimized = (employment.optimized_bullets || [])[index] || "";
        bulletChanges.set(normalizeResumeText(original), optimized);
      });
    });
    const skillLineChanges = new Map(
      (skillChanges.original_skills || []).map((line, index) => [
        normalizeResumeText(line),
        (skillChanges.optimized_skills || [])[index] || line,
      ]),
    );
    const summaryChanges = optimization.summary_changes || {};
    const summaryLineIndexes = findResumeLineBlock(
      originalLines,
      summaryChanges.original_summary,
    );
    const summaryLines = splitResumePreviewText(
      summaryChanges.optimized_summary,
      summaryLineIndexes.length,
    );
    const summaryChangesByLine = new Map(
      summaryLineIndexes.map((lineIndex, index) => [lineIndex, summaryLines[index] || ""]),
    );
    const originalResumeRole = normalizeResumeText(
      optimization.original_resume_role_title,
    );
    const optimizedResumeRole =
      optimization.employment_changes?.[0]?.optimized_role ||
      optimization.employment_changes?.[0]?.original_role ||
      "";
    const resumeHeadlineLineIndex = originalResumeRole
      ? originalLines.findIndex(
          (line) => normalizeResumeText(line) === originalResumeRole,
        )
      : -1;
    const employerLines = new Set(
      (optimization.employment_changes || [])
        .map((employment) => normalizeResumeText(employment.employer))
        .filter(Boolean),
    );
    const sectionHeadingPattern =
      /^(summary|professional summary|profile|professional profile|skills|technical skills|core skills|core competencies|experience|professional experience|work experience|employment|employment history)$/i;

    const renderDiff = (before, after) =>
      diffWords(before, after).map((change, index) => (
        <span className={`resume-diff-${change.type}`} key={index}>
          {change.text}
        </span>
      ));

    return (
      <div className="resumePreviewSection">
        <div className="resume-preview-heading">
          <div>
            <h2 className="title">Resume Document Preview</h2>
            <p>Review the tailored changes before updating your DOCX.</p>
          </div>
        </div>

        <div className="preview-action preview-action-top">
          <Button
            type="primary"
            onClick={finalizeResumeDocument}
            loading={previewLoading}
            disabled={!documentCompanyName.trim()}
          >
            Finalize Resume
          </Button>
        </div>

        <div className="resume-editor-card document-optimization-preview">
          <div className="field">
            <label className="label">Company name</label>
            <Input
              value={documentCompanyName}
              onChange={(event) => setDocumentCompanyName(event.target.value)}
              placeholder="Used for the saved DOCX and PDF filenames"
            />
          </div>
          <div className="resume-document-meta-grid">
            <div className="field">
              <label className="label">Role title</label>
              <Input
                value={optimization.role_title || ""}
                onChange={(event) =>
                  updateOptimizationField("role_title", event.target.value)
                }
              />
            </div>
            <div className="field">
              <label className="label">Salary range</label>
              <Input
                value={optimization.salary_range || ""}
                onChange={(event) =>
                  updateOptimizationField("salary_range", event.target.value)
                }
              />
            </div>
            <div className="field">
              <label className="label">Job type</label>
              <Input
                value={optimization.job_type || ""}
                onChange={(event) =>
                  updateOptimizationField("job_type", event.target.value)
                }
              />
            </div>
          </div>
          <Card title="Optimized summary">
            <TextArea
              rows={5}
              value={summaryChanges.optimized_summary || ""}
              onChange={(event) =>
                setOptimization((current) => ({
                  ...current,
                  summary_changes: {
                    ...(current.summary_changes || {}),
                    optimized_summary: event.target.value,
                  },
                }))
              }
            />
          </Card>
          <section
            className="resume-document-paper"
            aria-label="Resume document changes"
          >
            <div className="resume-document-paper-heading">
              <strong>Full resume preview</strong>
              <span>Red text will be removed; green text will be added.</span>
            </div>
            <div className="resume-document-page">
              {originalLines.map((line, index) => {
                const normalizedLine = normalizeResumeText(line);
                const optimizedBullet = bulletChanges.get(
                  normalizeResumeText(line),
                );
                const optimizedRole = roleChangesByLine.get(index);
                const optimizedSkill = skillLineChanges.get(
                  normalizeResumeText(line),
                );
                const optimizedSummary = summaryChangesByLine.has(index)
                  ? summaryChangesByLine.get(index)
                  : undefined;
                const optimizedHeadline =
                  index === resumeHeadlineLineIndex
                    ? optimizedResumeRole
                    : undefined;
                const replacement =
                  optimizedSummary !== undefined
                    ? optimizedSummary
                    :
                  optimizedHeadline ||
                  optimizedRole ||
                  optimizedSkill ||
                  optimizedBullet;
                const lineClasses = [
                  replacement !== undefined ? "resume-line-changed" : "",
                  sectionHeadingPattern.test(line.trim())
                    ? "resume-section-heading"
                    : "",
                  employerLines.has(normalizedLine)
                    ? "resume-employment-start"
                    : "",
                  !line.trim() ? "resume-blank-line" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <p className={lineClasses} key={index}>
                    {replacement !== undefined
                      ? renderDiff(line, replacement)
                      : line || "\u00a0"}
                  </p>
                );
              })}
              {(optimization.employment_changes || []).flatMap(
                (employment) => employment.added_bullets || [],
              ).length > 0 && (
                <div className="resume-added-content">
                  {(optimization.employment_changes || [])
                    .flatMap((employment) => employment.added_bullets || [])
                    .map((bullet, index) => (
                      <p className="resume-diff-added" key={index}>
                        • {bullet}
                      </p>
                    ))}
                </div>
              )}
            </div>
          </section>

          {(optimization.employment_changes || []).map(
            (employment, employmentIndex) => (
              <Card
                key={`${employment.employment_id || employment.employer}-${employmentIndex}`}
                title={`${employment.job_title || "Employment"}${employment.employer ? ` — ${employment.employer}` : ""}`}
              >
                <div className="field">
                  <label className="label">Optimized role</label>
                  <Input
                    value={employment.optimized_role || ""}
                    onChange={(event) =>
                      updateOptimizedRole(employmentIndex, event.target.value)
                    }
                  />
                </div>
                {(employment.optimized_bullets || []).map(
                  (bullet, bulletIndex) => (
                    <div className="field" key={bulletIndex}>
                      <label className="label">
                        Optimized bullet {bulletIndex + 1}
                      </label>
                      <TextArea
                        rows={2}
                        value={bullet}
                        onChange={(event) =>
                          updateOptimizedBullet(
                            employmentIndex,
                            bulletIndex,
                            event.target.value,
                          )
                        }
                      />
                    </div>
                  ),
                )}
                {(employment.removed_bullets || []).length > 0 && (
                  <div className="removed-content">
                    <strong>Removed</strong>
                    <ul>
                      {employment.removed_bullets.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            ),
          )}

          <Card title="Optimized skills">
            <div className="skills-preview-grid">
              {(skillChanges.optimized_skills || []).map((skill, index) => (
                <Input
                  key={index}
                  value={skill}
                  onChange={(event) =>
                    updateOptimizedSkill(index, event.target.value)
                  }
                />
              ))}
            </div>
          </Card>
        </div>

        <div className="preview-action">
          <Button
            type="primary"
            onClick={finalizeResumeDocument}
            loading={previewLoading}
            disabled={!documentCompanyName.trim()}
          >
            Finalize Resume
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="resumePreviewSection">
      <div className="resume-preview-heading">
        <div>
          <h2 className="title">
            <span className="resume-title-icon">
              <img src={ResumePreviewSrc} alt="title-logo" />
            </span>
            Resume Preview & Edit
          </h2>
          <p>Review and refine your tailored resume before finalizing it.</p>
        </div>
      </div>
      <div className="preview-action preview-action-top">
        <Button
          type="primary"
          htmlType="button"
          onClick={handleGenerateFinalizeResume}
          loading={previewLoading}
        >
          Finalize Resume
        </Button>
      </div>

      <div className="resume-editor-card">
        <div className="resume-meta-grid">
          <div className="field">
            <label className="label">Company:</label>
            <Input
              value={companyName.replace(/\.$/, "")}
              onChange={handleInputChange(setCompanyName)}
            />
          </div>
          <div className="field">
            <label className="label">Company Title:</label>
            <Input
              value={roleTitle}
              onChange={handleInputChange(setRoleTitle)}
            />
          </div>
          <div className="field">
            <label className="label">Job Type:</label>
            <Input value={jobType} onChange={handleInputChange(setJobType)} />
          </div>
          <div className="field">
            <label className="label">Role Title:</label>
            <Input
              value={developerTitle}
              onChange={handleInputChange(setDeveloperTitle)}
            />
          </div>
          <div className="field">
            <label className="label">Salary Range:</label>
            <Input
              value={salaryRange}
              onChange={handleInputChange(setSalaryRange)}
            />
          </div>
        </div>

        <div className="field">
          <label className="label">Summary:</label>
          <TextArea
            value={summary}
            onChange={handleInputChange(setSummary)}
            rows={3}
          />
        </div>

        <div
          className="employment-preview-grid"
          style={{ "--employment-columns": employmentPreferences.length }}
        >
          {employmentPreferences.map((employment, index) => {
            const roleKey = `role${index + 1}`;
            const experienceKey = `experience${index + 1}`;
            return (
              <section className="employment-preview-card" key={roleKey}>
                <div className="employment-preview-title">
                  <strong>Employment {index + 1}</strong>
                  {employment.title_prefix && (
                    <span>{employment.title_prefix}</span>
                  )}
                </div>
                <div className="field">
                  <label className="label">Role</label>
                  <Input
                    value={experiences[roleKey] || ""}
                    onChange={handleExperienceRoleChange(roleKey)}
                  />
                </div>

                <div className="field">
                  <label className="label">Experience</label>
                  {(experiences[experienceKey] || []).map((item, expIndex) => (
                    <div className="experience-entry" key={expIndex}>
                      <TextArea
                        rows={2}
                        value={item}
                        onChange={handleExperienceChange(
                          experienceKey,
                          expIndex,
                        )}
                      />
                      <Button
                        type="default"
                        danger
                        onClick={() =>
                          removeExperienceEntry(experienceKey, expIndex)
                        }
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="link"
                    onClick={() => addExperienceEntry(experienceKey)}
                  >
                    Add Experience Entry
                  </Button>
                </div>
              </section>
            );
          })}
        </div>

        <div className="field2">
          <label className="label">Skills:</label>
          <div className="skills-preview-grid">
            {Object.entries(skills).map(([category, items]) => (
              <Card
                key={category}
                title={category.replace("_", " ").toUpperCase()}
              >
                {items.map((skill, index) => (
                  <div className="skill-entry" key={index}>
                    <Input
                      value={skill}
                      onChange={handleSkillChange(category, index)}
                    />
                    <Button
                      type="default"
                      danger
                      onClick={() => removeSkillEntry(category, index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => addSkillEntry(category)}
                  style={{ width: "100%" }}
                >
                  Add Skill
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </div>

      <div className="preview-action">
        <Button
          type="primary"
          htmlType="button"
          onClick={handleGenerateFinalizeResume}
          loading={previewLoading}
        >
          Finalize Resume
        </Button>
      </div>
    </div>
  );
};

export default ResumePreview;
